import { FC, useContext, useEffect, useState } from 'react';

import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Button, Modal } from '@smartshell/ui-kit';
import { SubmitHandler, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { HOST_GROUPS } from 'schemas';
import notification from 'services/notificationService';
import { ETariffType } from 'types';
import { getActiveSeance } from 'utils';

import s from './HostTimeDownModal.module.scss';
import { FormValues, ICachedHost, IHostTimeDownModalProps } from './interfaces';
import { PENALTY_HOST } from './penalty-schema';
import { getMessage } from './utils';

export const HostTimeDownModal: FC<IHostTimeDownModalProps> = ({
  hostId,
  clientPenalty,
  onClose,
}) => {
  const { t } = useContext(LocalizationContext);
  const apolloClient = useApolloClient();

  const host = apolloClient.readQuery<ICachedHost>({
    id: apolloClient.cache.identify({
      id: hostId,
      __typename: 'HostOverview',
    }),
    query: gql`
      query hostOverview {
        alias

        user {
          nickname
        }

        client_sessions {
          id
          time_left

          seances {
            status

            tariff {
              kind
            }
          }
        }
      }
    `,
  });
  const activeSession = host?.client_sessions[0];

  const [remainingSeconds, setRemainingSeconds] = useState(
    activeSession?.time_left ?? 0
  );

  const penaltySeconds = clientPenalty * 60;
  const clientNickname = host?.user?.nickname;
  const activeSeance = getActiveSeance(activeSession?.seances || []);
  const isPerMinute = activeSeance?.tariff?.kind === ETariffType.PER_MINUTE;

  const [penaltyHost] = useMutation<void>(PENALTY_HOST, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      clientNickname
        ? notification.info({
            t,
            type: 'success',
            title: t('host.penalty.success', clientNickname, clientPenalty),
            leftDescription: host?.alias,
            dismiss: 5,
          })
        : notification.info({
            t,
            type: 'success',
            title: t('host.penalty.success.guest', clientPenalty),
            leftDescription: host?.alias,
            dismiss: 5,
          });

      onClose(0);
    },
    refetchQueries: [HOST_GROUPS],
    awaitRefetchQueries: true,
  });

  const onSubmit: SubmitHandler<FormValues> = (): Promise<unknown> => {
    return penaltyHost({
      variables: {
        input: { host_id: hostId, seconds: penaltySeconds },
      },
    });
  };

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<FormValues>({ mode: 'onChange' });

  useEffect(() => {
    const id = setInterval(() => {
      if (remainingSeconds) {
        setRemainingSeconds((remainingSeconds) => remainingSeconds - 1);
      }
    }, 100000);
    return () => {
      clearInterval(id);
    };
  }, [remainingSeconds]);

  const isPenaltyMode = penaltySeconds < remainingSeconds && !isPerMinute;

  return (
    <Modal onClose={() => onClose(0)}>
      <form className={s.root} onSubmit={handleSubmit(onSubmit)}>
        <span className={s.header}>
          {t('host.penalty.title', host?.alias ?? '')}
        </span>

        <span className={s.message}>
          {getMessage(
            t,
            host?.alias ?? '',
            isPerMinute,
            isPenaltyMode,
            clientPenalty,
            remainingSeconds
          )}
        </span>

        <div className={s.controls}>
          <Button
            className={s['cancel-penalty-button']}
            variant={isPerMinute ? 'primary' : 'outline'}
            fullWidth
            onClick={() => onClose(0)}
          >
            {t(isPerMinute ? 'ok' : 'host.penalty.decline')}
          </Button>

          {!isPerMinute && (
            <Button
              className={s['confirm-penalty-button']}
              color={isPenaltyMode ? 'blue' : 'red'}
              variant={isPenaltyMode ? 'primary' : 'outline'}
              type="submit"
              fullWidth
              isLoading={isSubmitting}
            >
              {t(isPenaltyMode ? 'host.penalty.submit' : 'host.penalty.finish')}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};
